/**
 * Per-tab remount generation, held OUTSIDE React state for the same reason as the optimistic
 * active-tab store: bumping one tab's generation must re-render only that tab's scene wrapper,
 * never the layout or the other scenes.
 *
 * Re-tapping a tab that is already on its base page remounts the base page in place by bumping
 * its generation, which the scene wrapper uses as a React `key`. Dispatching the tab's
 * `dismissTo` href there instead used to slide a fresh copy in from the right: when the base page
 * was reached through a redirect route (Browse's `index` → `(browse-tabs)`), the href resolves to a
 * route name that is no longer in the stack, so `POP_TO` replaces the top route with a pushed one.
 */
export type PrimaryNavigationSceneRemountStore = {
  bump: (name: string) => void;
  getGeneration: (name: string) => number;
  subscribe: (listener: () => void) => () => void;
};

export function createPrimaryNavigationSceneRemountStore(): PrimaryNavigationSceneRemountStore {
  const generations = new Map<string, number>();
  const listeners = new Set<() => void>();
  return {
    bump: (name) => {
      generations.set(name, (generations.get(name) ?? 0) + 1);
      for (const listener of listeners) {
        listener();
      }
    },
    getGeneration: (name) => generations.get(name) ?? 0,
    subscribe: (listener) => {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
  };
}
